import type { ShorthandValue, TileImageProps } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import type { TagType } from '~/modules/product-tagging/type';
import TagBarCarousel from './TagBarCarousel';
import type { FC } from 'react';
import { useEffect } from 'react';
import * as S from './TagBar.style';
import { getAdditionalVariantCount } from '~/modules/page-builder/util/get-additional-variant-count';
import { useTracker } from '@archipro-website/tracker';
import { useUser } from '~/modules/user';
import ProductTile from '~/modules/tile/component/product-tile/ProductImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import usePendingFetcherQueue from '@modules/root/hook/use-pending-fetcher-queue';
import type { OptimisticTags } from '@modules/product-tagging/type';
import useOptimisticTags from '@modules/product-tagging/hooks/use-optimistic-tags';
import VariantDot from '~/modules/tile/component/product-tile/VariantDot';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { useMatchesHandleData } from '~/modules/root';
import { useProductTagging } from '~/modules/product-tagging/context';

interface TagBarProps {
    tags: TagType[];
    className?: string;
    imageID?: number;
    disableClickOnTile?: boolean;
}

const TagBar: FC<TagBarProps> = ({
    tags,
    className,
    imageID = 0,
    disableClickOnTile = false,
}) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const user = useUser();
    const { canCreateTag } = useProductTagging();
    const isLoggedIn = user.__typename === 'Me';
    const isBMEdit = useMatchesHandleData<boolean>('isBMPreview', false);
    const { lastFetcher, addToFetcherQueue, clearFetcherQueue } =
        usePendingFetcherQueue<OptimisticTags>();

    const currentTags = lastFetcher?.tags ?? tags;
    const uniqueProductTags = currentTags.filter(
        (tag, index, self) =>
            self.findIndex((t) => t.product?.id === tag.product?.id) ===
                index &&
            (tag.approved || isBMEdit)
    );
    const optimisticTags = useOptimisticTags(imageID, uniqueProductTags);

    useEffect(() => {
        if (optimisticTags.fetcherKey) {
            addToFetcherQueue(optimisticTags);
        } else {
            clearFetcherQueue();
        }
    }, [addToFetcherQueue, clearFetcherQueue, optimisticTags]);

    const showTagsOnImage = useABTestIsOn('product_tagging_dots');
    if (showTagsOnImage && !canCreateTag) {
        return null;
    }

    const renderedTags = optimisticTags.tags.map((tag, index) => {
        const productTag = tag.product;

        if (!productTag) {
            return {};
        }

        const variants = productTag.variants;
        const variantsCount = productTag.variantsCount ?? 0;
        const additionalVariantCount =
            getAdditionalVariantCount(variantsCount) ?? 0;

        return {
            tag: productTag.tagId ?? index,
            productCard: (
                <>
                    <ProductTile
                        as={'a'}
                        href={productTag.link || ''}
                        target={'_blank'}
                        className={css(S.ProductTile)}
                        image={[
                            {
                                src: productTag.imageUrl ?? '',
                                sizes: getImagesSizes(186),
                                alt: productTag.title ?? '',
                            },
                        ]}
                        professionalName={productTag.professional}
                        productName={productTag.title}
                        pricePrefix={productTag.pricePrefix}
                        price={productTag.price}
                        variant="02"
                        size={'small'}
                        newDesignEnabled={true}
                        tileImage={
                            {
                                overlay: { topActions: [] },
                                images: [productTag.imageUrl],
                                className: css(({ theme }) =>
                                    S.ProductTagImage({
                                        theme,
                                        approved: tag.approved,
                                    })
                                ),
                            } as unknown as ShorthandValue<TileImageProps>
                        }
                        variables={{ padding: 0 }}
                        variants={variants?.map(({ id, label, code, dot }) => ({
                            key: `variant-dot-${id}`,
                            children: (
                                <VariantDot
                                    size={mobile ? 9 : 13}
                                    colorCode={code ?? ''}
                                    description={label}
                                    image={
                                        dot?.[mobile ? 'mobile' : 'desktop'] ??
                                        ''
                                    }
                                />
                            ),
                        }))}
                        additionalVariantCount={additionalVariantCount}
                        impression={{
                            professionalID: productTag.professionalID ?? 0,
                            itemID: productTag.id,
                            type: isLoggedIn
                                ? 'ProductTagLoggedIn'
                                : 'ProductTagLoggedOut',
                        }}
                        onClick={() => {
                            tracker.log('TagHoverClick', {
                                url: new URL(window.location.href),
                                targetTracker: [
                                    'archiproTracker',
                                    'ga4Tracker',
                                ],
                                data: {
                                    ExtraData: tag.id,
                                    professionalID: productTag.professionalID,
                                    productID: productTag.id,
                                    tagID: tag.id,
                                },
                                ga4Data: { tagId: tag.id },
                            });
                        }}
                        onMouseEnter={() => {
                            tracker.log('TagHoverReveal', {
                                url: new URL(window.location.href),
                                targetTracker: [
                                    'archiproTracker',
                                    'ga4Tracker',
                                ],
                                data: {
                                    ExtraData: tag.id,
                                },
                                ga4Data: { tagId: tag.id },
                            });
                        }}
                    />
                    {disableClickOnTile && (
                        <div className={css(S.BoxToDisableClickOnTile)} />
                    )}
                </>
            ),
        };
    });

    return (
        <TagBarCarousel
            tags={renderedTags}
            displayCount={9}
            className={className}
        />
    );
};

export default TagBar;
